/**
 * Block 12 PR-C Phase C7 — useFreeAnketaForm hook.
 *
 * Local useState-based state hook для FreeAnketaForm (Other anketa). Простая
 * shape — без useReducer (нет inter-field invariants, нет cross-state transitions).
 * Mirror useAnketaForm exposure: `{state, actions, total, isValid}`.
 *
 * **Derived values:**
 *   - `total = sum(row.quantity × row.price)` (decimal, не округляется;
 *     non-finite combinations отбрасываются как 0 — UI рендерит цифровое поле,
 *     но empty input → NaN иначе бы испортил total для всего проекта).
 *   - `isValid = projectName.trim() !== '' && freeRows.length >= 1 && каждый
 *     row имеет name.trim() ≠ '' и quantity > 0 и price ≥ 0` (zero-qty row
 *     bezzmysslen для сметы; price=0 OK для скидочных позиций).
 *
 * **`initialDraft` rehydration:** при presence — replaces useState initializer
 *  (как useAnketaForm). При отсутствии — `freshState(projectId)` (empty form).
 *
 * Reference: ADR-0020 §Q9 (`docs/architecture/adr/0020-anketa-form-template-architecture.md`).
 */

import { useCallback, useMemo, useState } from 'react';
import { genUuid } from '@/lib/uuid';
import type { FreeAnketaActions, FreeAnketaState, FreeRow, FreeRowEditableField } from './types';

function freshState(projectId: string): FreeAnketaState {
  const now = Date.now();
  return {
    projectId,
    projectName: '',
    freeRows: [],
    isDirty: false,
    createdAt: now,
    updatedAt: now,
  };
}

export interface UseFreeAnketaFormResult {
  state: FreeAnketaState;
  actions: FreeAnketaActions;
  total: number;
  isValid: boolean;
}

export function useFreeAnketaForm(
  projectId: string,
  initialDraft?: FreeAnketaState,
): UseFreeAnketaFormResult {
  const [state, setState] = useState<FreeAnketaState>(() => initialDraft ?? freshState(projectId));

  const setProjectName = useCallback((name: string) => {
    setState((prev) => ({
      ...prev,
      projectName: name,
      isDirty: true,
      updatedAt: Date.now(),
    }));
  }, []);

  const addRow = useCallback((): string => {
    const id = genUuid();
    setState((prev) => ({
      ...prev,
      freeRows: [...prev.freeRows, { id, name: '', quantity: 0, unit: 'шт', price: 0 }],
      isDirty: true,
      updatedAt: Date.now(),
    }));
    return id;
  }, []);

  const updateRow = useCallback(
    <F extends FreeRowEditableField>(id: string, field: F, value: FreeRow[F]) => {
      setState((prev) => ({
        ...prev,
        freeRows: prev.freeRows.map((r): FreeRow => (r.id === id ? { ...r, [field]: value } : r)),
        isDirty: true,
        updatedAt: Date.now(),
      }));
    },
    [],
  );

  const removeRow = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      freeRows: prev.freeRows.filter((r) => r.id !== id),
      isDirty: true,
      updatedAt: Date.now(),
    }));
  }, []);

  const actions: FreeAnketaActions = useMemo(
    () => ({ setProjectName, addRow, updateRow, removeRow }),
    [setProjectName, addRow, updateRow, removeRow],
  );

  // Defensive Number.isFinite guard: пустой `<input type="number">` отправляет
  // NaN при coerce; one NaN propagated в reduce закидывает весь total в NaN
  // и далее в IDB total field (P-009 frozen contract — total: z.number().nonnegative()
  // → schema rejection при subsequent persist). 0-fallback держит UI-render
  // безопасным до next valid input.
  const total = useMemo(
    () =>
      state.freeRows.reduce((acc, r) => {
        const subtotal = r.quantity * r.price;
        return acc + (Number.isFinite(subtotal) && subtotal >= 0 ? subtotal : 0);
      }, 0),
    [state.freeRows],
  );

  const isValid = useMemo(() => {
    if (state.projectName.trim() === '') return false;
    if (state.freeRows.length === 0) return false;
    return state.freeRows.every(
      (r) =>
        r.name.trim() !== '' &&
        Number.isFinite(r.quantity) &&
        r.quantity > 0 &&
        Number.isFinite(r.price) &&
        r.price >= 0,
    );
  }, [state.projectName, state.freeRows]);

  return { state, actions, total, isValid };
}
